import { useEffect, useState, useCallback } from 'react';
import { message, Modal } from 'antd';
import { observer } from 'mobx-react-lite';
import { useParams, useLocation } from 'react-router-dom';

import { useStores } from '@/hooks/use-stores';
import { STAGE_MESSAGES, STATION_STAGES_DEPRECATED } from '@/comp/StationStages/constants';
import { ProactiveEngineStateEnum, StationStageName } from '@/constants/enums';

import CurrentStageCardComponent from './CurrentStageCardComponent';
import DeprecateModal from './DeprecateModal';
import EngineValidationModal from './EngineValidationModal';
import { canShowCurrentStageCard } from './utils';
import StationStore from '@/store/Station/StationStore';
import ProactiveEngineStore from '@/store/ProactiveEngineStore';
import { EngineStore } from '@/store/Engine/EngineStore';
import EcoSelectStation from '../EcoSelectStation/EcoSelectStation';
import { ContentWrapper } from './styled';

interface StationStagesProps {
  isTransparent?: boolean;
}

const StationStages: React.FC<StationStagesProps> = observer(({ isTransparent = false }) => {
  const {
    stationStore,
    proactiveEngineStore,
    engineStore,
  }: { stationStore?: StationStore; proactiveEngineStore?: ProactiveEngineStore; engineStore?: EngineStore } =
    useStores();
  const { stationId } = useParams();
  const { pathname } = useLocation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEngineValidationModalVisible, setIsEngineValidationModalVisible] = useState(false);
  const { currentStationStage, stages } = stationStore;
  const isSccPath = pathname.includes('control-center');
  const [selectedStageInDropdown, setSelectedStageInDropdown] = useState(currentStationStage?.stage);
  const [currentStatus, setCurrentStatus] = useState(engineStore?.engineStatus?.status);
  useEffect(() => {
    const fetchData = async () => {
      await stationStore.fetchStationData(stationId);
      await engineStore.getEngineStatus();
      setSelectedStageInDropdown(currentStationStage?.stage);
      setCurrentStatus(engineStore?.engineStatus?.status);
    };
    fetchData();
  }, [
    stationStore,
    engineStore,
    stationId,
    currentStationStage?.stage?.id,
    proactiveEngineStore.proactiveEngineState,
    proactiveEngineStore.engineStatus,
  ]);

  const currentStage = currentStationStage?.stage;

  const handleStageChange = useCallback(
    async (stageId: number, forceUpdate = false) => {
      if (stageId === currentStage?.id) return;

      const selectedStage = stages.find(stage => stage.id === stageId);
      setSelectedStageInDropdown(selectedStage);
      const isMovingFromSubscription = currentStage?.name === StationStageName.Subscription;
      const isEngineActive = proactiveEngineStore.proactiveEngineState === ProactiveEngineStateEnum.Activated;
      const isMovingToDeprecated = selectedStage?.name === STATION_STAGES_DEPRECATED;

      // Check if moving from Subscription state with active engine (prioritize this over deprecation modal)
      if (isMovingFromSubscription && isEngineActive) {
        setIsEngineValidationModalVisible(true);
        return;
      }

      // Check if moving to deprecated stage (only show if engine is not active)
      if (isMovingToDeprecated && !forceUpdate && !isEngineActive) {
        setIsModalVisible(true);
        return;
      }

      try {
        message.loading({ content: STAGE_MESSAGES.updating, key: 'stageUpdate' });
        await stationStore.updateStationStage(stageId, stationId);
        message.success({ content: STAGE_MESSAGES.updated, key: 'stageUpdate' });
      } catch (error) {
        message.destroy('stageUpdate');
        Modal.error({
          title: STAGE_MESSAGES.error,
          content: STAGE_MESSAGES.error,
        });
      }
    },
    [currentStage, stationStore, stages, stationId, proactiveEngineStore.engineStatus],
  );

  const handleDeprecateConfirm = async () => {
    const deprecatedStage = stages.find(stage => stage.name === STATION_STAGES_DEPRECATED);
    if (deprecatedStage) {
      await handleStageChange(deprecatedStage.id, true);
    }
    setIsModalVisible(false);
  };

  const handleDeprecateCancel = async () => {
    setSelectedStageInDropdown(currentStationStage?.stage);
    setIsModalVisible(false);
  };

  const handleEngineValidationOk = () => {
    setIsEngineValidationModalVisible(false);
  };

  const { permissionStore } = useStores();
  if (!canShowCurrentStageCard(permissionStore, stationStore, currentStatus)) {
    return;
  }

  return (
    <>
      <ContentWrapper $isSccPath={isSccPath}>
        {isSccPath && <EcoSelectStation />}
        <CurrentStageCardComponent
          currentStage={currentStage}
          selectedStageInDropdown={selectedStageInDropdown}
          currentStatus={currentStatus}
          stages={stages}
          isLoading={stationStore.isLoadingStageData}
          handleStageChange={handleStageChange}
          stationId={stationId}
          isTransparent={isTransparent}
        />
      </ContentWrapper>
      <DeprecateModal isOpen={isModalVisible} onConfirm={handleDeprecateConfirm} onCancel={handleDeprecateCancel} />
      <EngineValidationModal isOpen={isEngineValidationModalVisible} onOk={handleEngineValidationOk} />
    </>
  );
});

export default StationStages;
